import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CurrencyInput } from "@/components/shared";
import { toInputDisplay, parseNumber } from "@/lib/format";
import { resizeImage } from "@/lib/image";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

// Schema-driven dialog form.
// field: { name, label, type: text|textarea|currency|select|date|time|checkbox|photos, options?, required?, disabled?, max? }
export default function DataDialog({ open, onOpenChange, title, fields, initial, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  useEffect(() => {
    if (open) {
      const init = {};
      fields.forEach((f) => {
        let v = initial ? initial[f.name] : undefined;
        if (f.type === "currency") v = v !== undefined && v !== null && v !== "" ? toInputDisplay(v) : "";
        else if (f.type === "checkbox") v = !!v;
        else if (f.type === "photos") v = Array.isArray(v) ? v : [];
        else v = v ?? "";
        init[f.name] = v;
      });
      setForm(init);
    }
  }, [open, initial, fields]);

  const set = (name, val) => setForm((p) => ({ ...p, [name]: val }));

  const handlePhotos = async (name, files, max = 10) => {
    if (!files || files.length === 0) return;
    const list = Array.isArray(form[name]) ? form[name] : [];
    const room = Math.max(0, max - list.length);
    if (room === 0) { toast.error(`Maksimal ${max} foto`); return; }
    setUploadingKey(name);
    try {
      const encoded = await Promise.all(Array.from(files).slice(0, room).map((f) => resizeImage(f, 1200)));
      set(name, [...list, ...encoded]);
    } catch (e) {
      toast.error("Gagal memproses foto");
    } finally {
      setUploadingKey("");
    }
  };

  const removePhoto = (name, idx) => {
    const list = Array.isArray(form[name]) ? [...form[name]] : [];
    list.splice(idx, 1);
    set(name, list);
  };

  const submit = async () => {
    for (const f of fields) {
      if (f.required && (form[f.name] === "" || form[f.name] === undefined || form[f.name] === null)) {
        toast.error(`${f.label} wajib diisi`);
        return;
      }
    }
    const payload = {};
    fields.forEach((f) => {
      if (f.type === "currency") payload[f.name] = parseNumber(form[f.name]);
      else if (f.type === "photos") payload[f.name] = Array.isArray(form[f.name]) ? form[f.name] : [];
      else payload[f.name] = form[f.name];
    });
    setSaving(true);
    try {
      await onSave(payload);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md" data-testid="data-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
          <DialogDescription className="sr-only">Formulir input data {title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={f.type === "checkbox" ? "flex items-center gap-3" : ""}>
              {f.type !== "checkbox" && <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}{f.required && " *"}</Label>}
              {f.type === "text" && (
                <Input data-testid={`field-${f.name}`} className="mt-1.5" disabled={f.disabled}
                  value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />
              )}
              {f.type === "date" && (
                <Input type="date" data-testid={`field-${f.name}`} className="mt-1.5"
                  value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />
              )}
              {f.type === "time" && (
                <Input type="time" data-testid={`field-${f.name}`} className="mt-1.5"
                  value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />
              )}
              {f.type === "textarea" && (
                <Textarea data-testid={`field-${f.name}`} className="mt-1.5"
                  value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />
              )}
              {f.type === "currency" && (
                <div className="mt-1.5">
                  <CurrencyInput data-testid={`field-${f.name}`} disabled={f.disabled}
                    value={form[f.name] || ""} onChange={(v) => set(f.name, v)} />
                </div>
              )}
              {f.type === "select" && (
                <Select value={form[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`field-${f.name}`} className="mt-1.5"><SelectValue placeholder={f.placeholder || "Pilih"} /></SelectTrigger>
                  <SelectContent>
                    {(f.options || []).map((o) => (
                      <SelectItem key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
                        {typeof o === "string" ? o : o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {f.type === "checkbox" && (
                <>
                  <Checkbox data-testid={`field-${f.name}`} checked={!!form[f.name]} onCheckedChange={(v) => set(f.name, !!v)} />
                  <Label className="text-sm">{f.label}</Label>
                </>
              )}
              {f.type === "photos" && (
                <div className="mt-1.5 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {(form[f.name] || []).map((src, idx) => (
                      <div key={idx} data-testid={`field-${f.name}-item-${idx}`} className="group relative h-20 w-20 overflow-hidden rounded-md border border-border bg-muted">
                        <img src={src} alt={`${f.name}-${idx}`} className="h-full w-full object-cover" />
                        <button
                          type="button"
                          data-testid={`field-${f.name}-remove-${idx}`}
                          onClick={() => removePhoto(f.name, idx)}
                          className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                          title="Hapus foto"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <label
                      data-testid={`field-${f.name}-add`}
                      className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                    >
                      {uploadingKey === f.name ? (
                        <span className="text-[10px]">Memuat…</span>
                      ) : (
                        <>
                          <ImagePlus className="h-5 w-5" />
                          <span className="mt-1 text-[10px]">Tambah</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => { handlePhotos(f.name, e.target.files, f.max || 10); e.target.value = ""; }}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {(form[f.name] || []).length}/{f.max || 10} foto — maks 1200px otomatis dikompres.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel">Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="dialog-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
